'use client';

import { Building2, Loader2 } from 'lucide-react';
import { useRouter } from '@/core/i18n/navigation';
import { useLeaseNowAvailability } from './useLeaseNowAvailability';

interface LeaseNowProductBadgeProps {
    product: any;
    variant?: any;
    quantity?: number;
}

export function LeaseNowProductBadge({ product, variant, quantity = 1 }: LeaseNowProductBadgeProps) {
    const router = useRouter();

    const activePrice = variant
        ? (variant.sales_price !== null && variant.sales_price !== undefined ? variant.sales_price : variant.price)
        : (product?.sales_price !== null && product?.sales_price !== undefined ? product?.sales_price : product?.price);

    // Shared with the builder's LeaseNow blocks — one availability request per product line.
    const { data: availabilityData, loading } = useLeaseNowAvailability(product, variant, quantity, true, {
        leasingPeriodMonths: 36,
        ownContributionPercent: 20,
        residualValuePercent: 20,
    });

    // Sleek skeleton loader while checking availability
    if (loading) {
        return (
            <div className="mt-3 p-4 bg-orange-50/40 rounded-2xl border border-orange-200/60 shadow-xs animate-pulse">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-500/20 text-orange-600 rounded-xl shrink-0">
                        <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
                    </div>
                    <div className="space-y-1.5 flex-1">
                        <div className="h-4 bg-orange-200/60 rounded-md w-1/3" />
                        <div className="h-3 bg-orange-200/40 rounded-md w-1/2" />
                    </div>
                </div>
            </div>
        );
    }

    // products[0].availability is LeaseNow's per-product eligibility flag — whether THIS item
    // (category/price/state) is a type of thing they'd ever finance at all — a separate concern
    // from the top-level `availability`, which only says whether the cart-so-far (just this one
    // product, on this badge) has already reached their minimum net amount. A product priced
    // well under that minimum still comes back with productAvailability:true and a real
    // missingNetAmount if it's a leasing-eligible category; ineligible categories come back
    // false regardless of price, and no amount of "add more" would ever change that.
    const productAvailability = availabilityData?.products?.[0]?.availability;

    if (productAvailability === false) {
        return null;
    }

    if (!availabilityData || !availabilityData.availability) {
        const missing = availabilityData?.missingNetAmount ?? 0;
        if (missing > 0) {
            return (
                <div className="mt-3 p-4 bg-amber-50/80 rounded-2xl border border-amber-200/80 shadow-xs space-y-2">
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-amber-500/20 text-amber-700 rounded-xl shrink-0 mt-0.5">
                            <Building2 className="h-5 w-5" />
                        </div>
                        <div className="text-xs text-slate-700 leading-relaxed">
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-sm text-slate-900">Leasing B2B (od 1000 zł netto)</span>
                                <span className="px-2 py-0.5 bg-amber-600 text-white text-[9px] font-black uppercase rounded-full">Firmy</span>
                            </div>
                            <p className="mt-1 text-slate-700 font-medium">
                                Do opcji leasingu B2B brakuje jeszcze <strong className="text-amber-800 font-bold">{missing > 0 ? missing.toFixed(2) : '0.00'} PLN netto</strong>.
                            </p>
                            <p className="mt-0.5 text-slate-500">
                                Zwiększ liczbę sztuk lub dodaj kolejne produkty do koszyka, aby wygenerować wniosek leasingowy.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }

    const monthlyRate = availabilityData.products?.[0]?.monthlyPaymentNet;

    const handleLeasingClick = () => {
        // Add product to cart first so item is saved
        const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
        const cartItemId = variant ? `${product.id}-${variant.id}` : `${product.id}`;
        const existingItem = cart.find((item: any) => item.cartItemId === cartItemId);

        if (!existingItem) {
            cart.push({
                cartItemId,
                id: product.id,
                variant_id: variant?.id || null,
                name: product.name + (variant ? ` (${Object.values(variant.attributes).join(', ')})` : ''),
                price: activePrice,
                image: product.images?.[0]?.path ? `/static/products/${product.images[0].path}` : null,
                slug: product.slug,
                quantity: quantity,
                attributes: variant?.attributes || null,
                is_one_off: !!product.is_one_off
            });
            localStorage.setItem('stag-cart', JSON.stringify(cart));
            window.dispatchEvent(new Event('cart-updated'));
        }


        if (availabilityData?.redirectUrl) {
            window.open(availabilityData.redirectUrl, '_blank');
        } else {
            router.push('/zamowienie?paymentMethod=leasenow');
        }
    };

    return (
        <div className="mt-3 p-4 bg-gradient-to-r from-orange-500/10 via-amber-500/5 to-orange-500/10 rounded-2xl border border-orange-200 shadow-sm transition-all hover:border-orange-300 space-y-3">
            <div className="flex items-center gap-3">
                <div className="p-2.5 bg-orange-600 text-white rounded-xl shadow-sm shrink-0">
                    <Building2 className="h-5 w-5" />
                </div>
                <div>
                    <div className="flex items-center gap-2">
                        <span className="font-extrabold text-sm text-slate-900">Leasing B2B</span>
                        <span className="px-2 py-0.5 bg-orange-600 text-white text-[10px] font-black uppercase rounded-full">Firmy</span>
                    </div>
                    <p className="text-xs text-slate-600 font-medium mt-0.5">
                        {typeof monthlyRate === 'number' ? (
                            <>Rata od <strong className="text-orange-700 font-bold">{monthlyRate.toFixed(2)} PLN</strong> netto / mies.</>
                        ) : (
                            <>Weź ten przedmiot w leasing dla firm</>
                        )}
                    </p>
                </div>
            </div>

            {/* Official Button Image from Lease Now API */}
            <div className="pt-1">
                <button
                    type="button"
                    onClick={handleLeasingClick}
                    aria-label="ING Lease Now"
                    className="inline-block w-[260px] max-w-full transition-transform hover:scale-[1.02] active:scale-[0.98]"
                >
                    <img
                        alt="ING Lease Now"
                        className="leasenow_button-image"
                        src="https://leasenow.pl/assets/ecommerce/images/leasing_button.png"
                        style={{ display: 'block', width: '100%', height: 'auto' }}
                    />
                </button>
            </div>
        </div>
    );
}
